import { createStore, type StoreApi } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Access } from '@/lib/sharing/utils/permissions';
import type { RecordingMeta } from './entity-events/recording';
import { isCanvasMetaRow, isCollectionRow, isConversationRow, isEntityMetaRow, isSnapshotInfo, isSpaceMetaRow } from './entity-event-validators';

// Migration: delete the old HttpSyncQueue IDB database
if (typeof indexedDB !== 'undefined') {
  indexedDB.deleteDatabase('nula-http-queue');
}

// ---------------------------------------------------------------------------
// Row types — mirror the server-side entity shapes
//
// M9.5-Unify Task 8: every entity kind (document, collection,
// canvas, space) ships the same cross-cutting field set. `BaseEntityRow` is
// the shared shape; per-kind types narrow `kind` and add kind-specific
// fields. Recording stays bespoke (its payload is dominated by
// recording-specific fields and is intentionally outside the unification).
// ---------------------------------------------------------------------------

export interface BaseEntityRow {
  id: string;
  kind: 'document' | 'collection' | 'canvas' | 'space' | 'conversation';
  title: string;
  snippet: string;
  isArchived: boolean;
  tags: string[];
  collectionIds: string[];
  previewMarkup: string | null;
  previewKind: 'html' | 'svg' | null;
  // version is optional: conversations omit it (M1 removed it from the
  // conversation sync payload). Other kinds include it. When present it is a
  // number; absent is valid (isBaseEntityRow treats it as optional).
  version?: number;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  access: Access;
}

export type EntityMetaRow = BaseEntityRow & { kind: 'document' };
export type CollectionRow = BaseEntityRow & { kind: 'collection' };
export type CanvasMetaRow = BaseEntityRow & { kind: 'canvas' };

export interface SpaceMetaRow extends BaseEntityRow {
  kind: 'space';
  isDefault: boolean;
}

export interface ConversationRow extends BaseEntityRow {
  kind: 'conversation';
  isPinned: boolean;
  latestActivityAt: string;
}

export interface SnapshotInfo {
  id: string;
  entityId: string;
  trigger: string;
  name: string | null;
  timestamp: string;
}

export interface EntityEventPayload {
  /** Opaque event id used as cursor position. */
  id: string;
  /**
   * Event kind string, as emitted by `entity_events.rs` on the server.
   *
   * S7 normalized all entity events to bare-caps format. S9 hardening 3
   * removed the deprecated `recording.upserted` / `recording.deleted`
   * aliases — server normalization has been confirmed in production.
   *   - `UPSERTED` / `DELETED` — used for all entity kinds (document,
   *     collection, snapshot, recording, canvas, space).
   *   - `recording.started` — cross-device suppression signal only (session 27).
   *     Not a store event; forwarded to Rust via invoke in entityEventHandler.
   */
  kind:
    | 'UPSERTED'
    | 'DELETED'
    | 'recording.started';
  modelKind: 'document' | 'collection' | 'snapshot' | 'recording' | 'canvas' | 'space' | 'conversation';
  modelId: string;
  data: Record<string, unknown> | null;
}

/**
 * Typed entity op — Wire Protocol C.
 *
 * Discriminated union by `action`. Mirrors the server-side `Op` enum in
 * `ws/envelope.rs`. The `opId` is NOT carried here — it lives on the
 * enclosing `PendingOp.opId` and is injected by `syncEngine.sendOps`
 * during encoding.
 *
 * Action names use camelCase to match the server's `#[serde(rename_all = "camelCase")]`.
 */
export type Op =
  | { action: 'create'; entityId: string; kind: 'document' | 'collection' | 'recording' | 'canvas' | 'space'; title: string }
  | { action: 'rename'; entityId: string; title: string; version: number }
  | { action: 'archive'; entityId: string; archived: boolean; version: number }
  | { action: 'restore'; entityId: string; version: number }
  | { action: 'delete'; entityId: string; version: number }
  | { action: 'setTags'; entityId: string; tags: string[]; version: number }
  | { action: 'setCollectionIds'; entityId: string; collectionIds: string[]; version: number }
  | { action: 'updatePreview'; entityId: string; markup: string | null; kind: string | null; version: number };

/**
 * Snapshot op — travels the dedicated `KIND_SNAPSHOT_OP` envelope rail.
 * Snapshots are not entity-table rows so they don't carry `entityKind`;
 * the envelope kind byte is the discriminator.
 *
 * `entityId` semantics depend on `action`:
 *   * `create` — the parent document id; the new snapshot id is supplied via
 *     `fields.snapshotId` or generated by the DB.
 *   * `update` / `delete` / `restore` — the snapshot id.
 */
export interface SnapshotOp {
  action: 'create' | 'update' | 'delete' | 'restore';
  entityId: string;
  fields?: Record<string, unknown>;
}

export interface PendingOp {
  opId: string;
  ops: Op[];
  createdAt: number;
  /** Ephemeral — true if the op has been sent over WS in this session. Not persisted. */
  sent: boolean;
}

/** Persistent snapshot-op batch — same lifecycle as PendingOp but for the snapshot rail. */
export interface PendingSnapshotOp {
  opId: string;
  ops: SnapshotOp[];
  createdAt: number;
  sent: boolean;
}

export interface OpResult {
  opId: string;
  status: 'ok' | 'conflict' | 'error' | 'permissionDenied';
  entityId: string;
  version?: number;
  serverVersion?: number;
  message?: string;
  /** Optional data payload from read-style ops (e.g. snapshot preview returns state bytes as base64). */
  data?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export type { RecordingMeta };

interface EntityStoreState {
  entityMeta: Map<string, EntityMetaRow>;
  collections: Map<string, CollectionRow>;
  snapshots: Map<string, SnapshotInfo>;
  recordings: Map<string, RecordingMeta>;
  /** M9.5-Unify Task 12: canvas-kind entity Map. */
  canvases: Map<string, CanvasMetaRow>;
  /** M9.5-Unify Task 12: space-kind entity Map. */
  spaces: Map<string, SpaceMetaRow>;
  /** M3: conversation-kind entity Map (private-by-default). */
  conversations: Map<string, ConversationRow>;
  cursor: string | null;
  pendingOps: PendingOp[];
  /**
   * Snapshot-op queue. Parallel to `pendingOps` but travels the
   * `KIND_SNAPSHOT_OP` rail instead of `KIND_OPS`.
   */
  pendingSnapshotOps: PendingSnapshotOp[];
  applyEvent: (event: EntityEventPayload) => void;
  applyBatch: (events: EntityEventPayload[], cursor: string) => void;
  addOp: (op: PendingOp) => void;
  /** Enqueue a snapshot-op batch for delivery via `KIND_SNAPSHOT_OP`. */
  addSnapshotOp: (op: PendingSnapshotOp) => void;
  confirmOp: (opId: string, results: OpResult[]) => void;
  discardOp: (opId: string) => void;
  markOpSent: (opId: string) => void;
  /** Mark a queued snapshot-op as sent (per-session ephemeral flag). */
  markSnapshotOpSent: (opId: string) => void;
  markAllUnsent: () => void;
  reset: () => void;
  resetEntities: () => void;
  /**
   * Atomic bootstrap step: wipe all entity Maps AND apply a server batch
   * in a single set() call. Prevents subscribers from observing the
   * transient empty state that the old `resetEntities()` → `applyBatch()`
   * pair produced. Cursor + pendingOps are preserved across the wipe
   * (matches the resetEntities contract).
   */
  resetAndApplyBatch: (events: EntityEventPayload[], cursor: string) => void;
  reapplyPendingOps: () => void;
  /**
   * Called when the server returns permissionDenied for an op.
   * Removes the op from pendingOps and resets the cursor to null so the next
   * connect triggers a fresh bootstrap, which will restore any optimistically
   * mutated entities to their server-authoritative state.
   *
   * Do NOT re-apply the op's fields — the entity store may be optimistically
   * stale; the resolver relies on the next bootstrap/sync to correct it.
   */
  rejectOp: (opId: string) => void;
  /**
   * Resolve a PendingOp whose sub-op results are MIXED (some ok, some
   * rejected). Always removes the pendingOp from the queue. Applies version
   * updates for ok sub-ops just like `confirmOp`, and — if any sub-op was
   * server-rejected on an existing entity — resets the cursor so the next
   * connect triggers a bootstrap that will restore optimistically-stale
   * state. Use for batches that aren't uniformly ok or uniformly rejected.
   */
  partialConfirm: (opId: string, results: OpResult[]) => void;
  /**
   * Register a callback invoked whenever an entity is removed (DELETED event)
   * or archived (UPSERTED with isArchived: true). Registered once by
   * SessionContext on mount. Replaces per-call-site closeTab() scatter.
   */
  setEntityRemovedCallback: (cb: (id: string) => void) => void;
}

function applyOneEvent(
  state: Pick<EntityStoreState, 'entityMeta' | 'collections' | 'snapshots' | 'recordings' | 'canvases' | 'spaces' | 'conversations'>,
  event: EntityEventPayload,
): void {
  const { kind, modelKind, modelId, data } = event;

  // S7 normalized all events to bare-caps UPSERTED / DELETED.
  // S9 hardening 3 removed the deprecated dotted-recording aliases —
  // server normalization is now mandatory.
  const normalizedKind = kind;

  if (normalizedKind === 'UPSERTED' && data) {
    if (modelKind === 'document') {
      if (!isEntityMetaRow(data)) {
        console.error('[entityStore] dropped malformed document UPSERTED payload for', modelId, data);
        return;
      }
      state.entityMeta.set(modelId, data);
    } else if (modelKind === 'collection') {
      if (!isCollectionRow(data)) {
        console.error('[entityStore] dropped malformed collection UPSERTED payload for', modelId, data);
        return;
      }
      state.collections.set(modelId, data);
    } else if (modelKind === 'snapshot') {
      if (!isSnapshotInfo(data)) {
        console.error('[entityStore] dropped malformed snapshot UPSERTED payload for', modelId, data);
        return;
      }
      state.snapshots.set(modelId, data);
    } else if (modelKind === 'recording') {
      // Bare UPSERTED carries the full RecordingMeta row (S7 normalization).
      // Bootstrap and live state-change events both use this convention.
      state.recordings.set(modelId, data as unknown as RecordingMeta);
    } else if (modelKind === 'canvas') {
      // M9.5-Unify Task 13: canvas UPSERTED handling.
      if (!isCanvasMetaRow(data)) {
        console.error('[entityStore] dropped malformed canvas UPSERTED payload for', modelId, data);
        return;
      }
      state.canvases.set(modelId, data);
    } else if (modelKind === 'space') {
      // M9.5-Unify Task 13: space UPSERTED handling.
      if (!isSpaceMetaRow(data)) {
        console.error('[entityStore] dropped malformed space UPSERTED payload for', modelId, data);
        return;
      }
      state.spaces.set(modelId, data);
    } else if (modelKind === 'conversation') {
      // M3: conversation UPSERTED handling (private-by-default sync).
      if (!isConversationRow(data)) {
        console.error('[entityStore] dropped malformed conversation UPSERTED payload for', modelId, data);
        return;
      }
      state.conversations.set(modelId, data);
    }
  } else if (normalizedKind === 'DELETED') {
    if (modelKind === 'document') {
      state.entityMeta.delete(modelId);
    } else if (modelKind === 'collection') {
      state.collections.delete(modelId);
    } else if (modelKind === 'snapshot') {
      state.snapshots.delete(modelId);
    } else if (modelKind === 'recording') {
      state.recordings.delete(modelId);
    } else if (modelKind === 'canvas') {
      // M9.5-Unify Task 14: canvas DELETED handling.
      state.canvases.delete(modelId);
    } else if (modelKind === 'space') {
      // M9.5-Unify Task 14: space DELETED handling.
      state.spaces.delete(modelId);
    } else if (modelKind === 'conversation') {
      // M3: conversation DELETED handling.
      state.conversations.delete(modelId);
    }
  }
}

// ---------------------------------------------------------------------------
// Map serialization helpers for JSON storage
// ---------------------------------------------------------------------------

export function mapReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    return { __type: 'Map', entries: Array.from(value.entries()) };
  }
  return value;
}

export function mapReviver(_key: string, value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    '__type' in value &&
    (value as Record<string, unknown>).__type === 'Map'
  ) {
    return new Map((value as unknown as { entries: [unknown, unknown][] }).entries);
  }
  return value;
}

// ---------------------------------------------------------------------------
// IndexedDB storage adapter for Zustand persist — per-workspace factory
// ---------------------------------------------------------------------------

const IDB_AVAILABLE = typeof indexedDB !== 'undefined';
const IDB_STORE = 'kv';

/**
 * Returns the IndexedDB database name for the given workspaceId.
 * Exported so clearWorkspaceData.ts can delete the right IDB by name.
 */
export function getDbName(workspaceId: string): string {
  return `nula-entity-store-${workspaceId}`;
}

// Per-dbName cache of the open IDBDatabase promise. Zustand persist
// writes on every state change; without the cache, every set/get/remove
// opens a fresh connection and the browser serializes them. The cache
// keeps a single long-lived handle per workspace.
//
// The cached promise is dropped if the connection closes (db.onclose)
// so the next call reopens. We don't proactively close — IDB handles
// stay open for the lifetime of the page, which matches the workspace
// store lifetime.
const idbConnectionCache = new Map<string, Promise<IDBDatabase>>();

function openIdbFor(dbName: string): Promise<IDBDatabase> {
  const cached = idbConnectionCache.get(dbName);
  if (cached) return cached;
  const promise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(dbName, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => {
      const db = req.result;
      // Drop the cached entry if the connection closes underneath us so
      // the next call reopens rather than reusing a dead handle.
      db.onclose = () => {
        if (idbConnectionCache.get(dbName) === promise) {
          idbConnectionCache.delete(dbName);
        }
      };
      resolve(db);
    };
    req.onerror = () => {
      // Don't keep a rejected promise in the cache — let the next call
      // retry from scratch.
      if (idbConnectionCache.get(dbName) === promise) {
        idbConnectionCache.delete(dbName);
      }
      reject(req.error);
    };
  });
  idbConnectionCache.set(dbName, promise);
  return promise;
}

// Exported for tests — see __tests__/entityStore.idb-pool.test.ts.
export function createIdbStorage(dbName: string): import('zustand/middleware').StateStorage {
  return {
    async getItem(name: string): Promise<string | null> {
      const db = await openIdbFor(dbName);
      return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readonly');
        const req = tx.objectStore(IDB_STORE).get(name);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => reject(req.error);
      });
    },
    async setItem(name: string, value: string): Promise<void> {
      const db = await openIdbFor(dbName);
      return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).put(value, name);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
    async removeItem(name: string): Promise<void> {
      const db = await openIdbFor(dbName);
      return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).delete(name);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
  };
}

// ---------------------------------------------------------------------------
// Migration
// ---------------------------------------------------------------------------

/**
 * Persist middleware migration function. Exported for unit testing.
 *
 * v1 → v2: Legacy rows lack access/workspaceId/createdAt. Reset entity maps
 * and cursor to force a fresh bootstrap on next connect. Pending ops are
 * preserved so in-flight user work survives the migration.
 *
 * v2 → v3: a now-removed kind Map was added historically. The Map is
 * dropped (M4); any persisted key for the removed kind is dropped on
 * rehydrate.
 *
 * Pre-launch migration freedom (CLAUDE.md): we silently drop the
 * removed-kind key from any persisted slice without a version bump. The
 * next bootstrap will reconcile remaining entity state.
 */
export function migrateEntityStore(
  persistedState: unknown,
  version: number,
): {
  entityMeta: Map<string, EntityMetaRow>;
  collections: Map<string, CollectionRow>;
  snapshots: Map<string, SnapshotInfo>;
  recordings: Map<string, RecordingMeta>;
  canvases: Map<string, CanvasMetaRow>;
  spaces: Map<string, SpaceMetaRow>;
  conversations: Map<string, ConversationRow>;
  cursor: string | null;
  pendingOps: PendingOp[];
  pendingSnapshotOps: PendingSnapshotOp[];
} {
  const state = (persistedState ?? {}) as Partial<{
    entityMeta: Map<string, EntityMetaRow>;
    collections: Map<string, CollectionRow>;
    snapshots: Map<string, SnapshotInfo>;
    recordings: Map<string, RecordingMeta>;
    canvases: Map<string, CanvasMetaRow>;
    spaces: Map<string, SpaceMetaRow>;
    conversations: Map<string, ConversationRow>;
    cursor: string | null;
    pendingOps: PendingOp[];
    pendingSnapshotOps: PendingSnapshotOp[];
  }>;

  if (version < 2) {
    // v1→v2: legacy rows lack access/workspaceId/createdAt. Full reset.
    return {
      entityMeta: new Map(),
      collections: new Map(),
      snapshots: new Map(),
      recordings: new Map(),
      canvases: new Map(),
      spaces: new Map(),
      conversations: new Map(),
      cursor: null,
      pendingOps: state.pendingOps ?? [],
      pendingSnapshotOps: [],
    };
  }

  if (version < 3) {
    // v2→v3: a now-removed kind Map was added historically (M4 removed it
    // again). Graceful reset of entity maps + cursor.
    return {
      entityMeta: new Map(),
      collections: new Map(),
      snapshots: new Map(),
      recordings: new Map(),
      canvases: new Map(),
      spaces: new Map(),
      conversations: new Map(),
      cursor: null,
      pendingOps: state.pendingOps ?? [],
      pendingSnapshotOps: [],
    };
  }

  if (version < 4) {
    // v3→v4: snapshot ops moved to a dedicated rail (pendingSnapshotOps +
    // KIND_SNAPSHOT_OP envelope). Drop any v3 pendingOps that were
    // entityKind: 'snapshot' since they can't be replayed on the entity-table
    // rail anymore — the next bootstrap will reconstruct snapshot state.
    const legacyPending = (state.pendingOps ?? []) as Array<{
      ops: Array<{ entityKind?: string }>;
    }>;
    const filteredPending = legacyPending.filter(
      (p) => !p.ops.some((o) => o.entityKind === 'snapshot'),
    ) as PendingOp[];
    return {
      entityMeta: state.entityMeta ?? new Map(),
      collections: state.collections ?? new Map(),
      snapshots: state.snapshots ?? new Map(),
      recordings: state.recordings ?? new Map(),
      canvases: state.canvases ?? new Map(),
      spaces: state.spaces ?? new Map(),
      conversations: state.conversations ?? new Map(),
      cursor: state.cursor ?? null,
      pendingOps: filteredPending,
      pendingSnapshotOps: [],
    };
  }

  if (version < 5) {
    // v4→v5: Wire Protocol C — typed op variants replace the generic
    // `{ action: 'update', fields: {...} }` shape. Old-format pendingOps
    // cannot be replayed with the new typed dispatch, so they are dropped.
    // The next bootstrap will restore server-authoritative state.
    return {
      entityMeta: state.entityMeta ?? new Map(),
      collections: state.collections ?? new Map(),
      snapshots: state.snapshots ?? new Map(),
      recordings: state.recordings ?? new Map(),
      canvases: state.canvases ?? new Map(),
      spaces: state.spaces ?? new Map(),
      conversations: state.conversations ?? new Map(),
      cursor: state.cursor ?? null,
      pendingOps: [],
      pendingSnapshotOps: state.pendingSnapshotOps ?? [],
    };
  }

  // v5+: pass through as-is (graceful defaults for any missing key).
  // Any persisted key for the removed kind is silently dropped (M4).
  // conversations: new in M3, defaults to empty Map for older persisted state.
  return {
    entityMeta: state.entityMeta ?? new Map(),
    collections: state.collections ?? new Map(),
    snapshots: state.snapshots ?? new Map(),
    recordings: state.recordings ?? new Map(),
    canvases: state.canvases ?? new Map(),
    spaces: state.spaces ?? new Map(),
    conversations: state.conversations ?? new Map(),
    cursor: state.cursor ?? null,
    pendingOps: state.pendingOps ?? [],
    pendingSnapshotOps: state.pendingSnapshotOps ?? [],
  };
}

// ---------------------------------------------------------------------------
// Store factory — per-workspace IDB
// ---------------------------------------------------------------------------

// Stored outside Zustand state so registering it doesn't trigger re-renders.
let _onEntityRemoved: ((id: string) => void) | null = null;

/**
 * Create a fresh Zustand vanilla store for the given workspaceId, backed by an
 * IDB named `nula-entity-store-{workspaceId}`. Each workspace gets its own
 * isolated local cache.
 *
 * The persist `name` field is the IDB key used by the storage adapter. We set
 * it to the workspaceId so the key is stable and deterministic.
 */
export function createEntityStore(workspaceId: string): StoreApi<EntityStoreState> & { persist: { getOptions(): { name: string }; rehydrate(): Promise<void> } } {
  const dbName = getDbName(workspaceId);
  return createStore<EntityStoreState>()(
    persist(
      (set) => ({
        entityMeta: new Map(),
        collections: new Map(),
        snapshots: new Map(),
        recordings: new Map(),
        canvases: new Map(),
        spaces: new Map(),
        conversations: new Map(),
        cursor: null,
        pendingOps: [],
        pendingSnapshotOps: [],

      applyEvent(event: EntityEventPayload) {
        set((state) => {
          const entityMeta = new Map(state.entityMeta);
          const collections = new Map(state.collections);
          const snapshots = new Map(state.snapshots);
          const recordings = new Map(state.recordings);
          const canvases = new Map(state.canvases);
          const spaces = new Map(state.spaces);
          const conversations = new Map(state.conversations);
          applyOneEvent({ entityMeta, collections, snapshots, recordings, canvases, spaces, conversations }, event);
          return { entityMeta, collections, snapshots, recordings, canvases, spaces, conversations };
        });
        if (event.kind === 'DELETED') {
          _onEntityRemoved?.(event.modelId);
        } else if (event.kind === 'UPSERTED' && (event.data as Record<string, unknown> | null)?.['isArchived'] === true) {
          _onEntityRemoved?.(event.modelId);
        }
      },

      applyBatch(events: EntityEventPayload[], cursor: string) {
        set((state) => {
          const entityMeta = new Map(state.entityMeta);
          const collections = new Map(state.collections);
          const snapshots = new Map(state.snapshots);
          const recordings = new Map(state.recordings);
          const canvases = new Map(state.canvases);
          const spaces = new Map(state.spaces);
          const conversations = new Map(state.conversations);
          for (const event of events) {
            applyOneEvent({ entityMeta, collections, snapshots, recordings, canvases, spaces, conversations }, event);
          }
          return { entityMeta, collections, snapshots, recordings, canvases, spaces, conversations, cursor };
        });
      },

      addOp(op: PendingOp) {
        set((state) => ({
          pendingOps: [...state.pendingOps, op],
        }));
      },

      addSnapshotOp(op: PendingSnapshotOp) {
        set((state) => ({
          pendingSnapshotOps: [...state.pendingSnapshotOps, op],
        }));
      },

      confirmOp(opId: string, results: OpResult[]) {
        set((state) => {
          const pendingOps = state.pendingOps.filter(op => op.opId !== opId);
          const pendingSnapshotOps = state.pendingSnapshotOps.filter(op => op.opId !== opId);
          const entityMeta = new Map(state.entityMeta);
          const collections = new Map(state.collections);
          const canvases = new Map(state.canvases);
          const spaces = new Map(state.spaces);
          const recordings = new Map(state.recordings);
          for (const r of results) {
            if (r.status === 'ok' && r.version != null) {
              const doc = entityMeta.get(r.entityId);
              if (doc) {
                entityMeta.set(r.entityId, { ...doc, version: r.version });
              }
              const col = collections.get(r.entityId);
              if (col) {
                collections.set(r.entityId, { ...col, version: r.version });
              }
              const canvas = canvases.get(r.entityId);
              if (canvas) {
                canvases.set(r.entityId, { ...canvas, version: r.version });
              }
              const space = spaces.get(r.entityId);
              if (space) {
                spaces.set(r.entityId, { ...space, version: r.version });
              }
              // RecordingMeta does not have a version field — skip version bump for recordings.
              // The server will reconcile recording state via the next UPSERTED event.
            }
          }
          return { pendingOps, pendingSnapshotOps, entityMeta, collections, canvases, spaces, recordings };
        });
      },

      discardOp(opId: string) {
        set((state) => ({
          pendingOps: state.pendingOps.filter(op => op.opId !== opId),
          pendingSnapshotOps: state.pendingSnapshotOps.filter(op => op.opId !== opId),
        }));
      },

      rejectOp(opId: string) {
        set((state) => ({
          pendingOps: state.pendingOps.filter(op => op.opId !== opId),
          pendingSnapshotOps: state.pendingSnapshotOps.filter(op => op.opId !== opId),
          // Reset cursor to null — next WS connect will send { type: 'sync', after: null }
          // which triggers a full bootstrap, restoring any optimistically mutated entities
          // to their server-authoritative state.
          cursor: null,
        }));
      },

      partialConfirm(opId: string, results: OpResult[]) {
        set((state) => {
          const pendingOps = state.pendingOps.filter(op => op.opId !== opId);
          const pendingSnapshotOps = state.pendingSnapshotOps.filter(op => op.opId !== opId);
          const entityMeta = new Map(state.entityMeta);
          const collections = new Map(state.collections);
          const canvases = new Map(state.canvases);
          const spaces = new Map(state.spaces);
          const recordings = new Map(state.recordings);
          let needsBootstrap = false;
          for (const r of results) {
            if (r.status === 'ok' && r.version != null) {
              const doc = entityMeta.get(r.entityId);
              if (doc) {
                entityMeta.set(r.entityId, { ...doc, version: r.version });
              }
              const col = collections.get(r.entityId);
              if (col) {
                collections.set(r.entityId, { ...col, version: r.version });
              }
              const canvas = canvases.get(r.entityId);
              if (canvas) {
                canvases.set(r.entityId, { ...canvas, version: r.version });
              }
              const space = spaces.get(r.entityId);
              if (space) {
                spaces.set(r.entityId, { ...space, version: r.version });
              }
              // RecordingMeta does not have a version field — skip version bump for recordings.
            } else if (r.status === 'permissionDenied' || r.status === 'conflict' || r.status === 'error') {
              needsBootstrap = true;
            }
          }
          // Reset cursor so the next reconnect triggers a bootstrap that
          // restores any optimistically-stale entities to authoritative state.
          // Mirrors rejectOp's mechanism but only when a real rejection occurred.
          return {
            pendingOps,
            pendingSnapshotOps,
            entityMeta,
            collections,
            canvases,
            spaces,
            recordings,
            ...(needsBootstrap ? { cursor: null } : {}),
          };
        });
      },

      markOpSent(opId: string) {
        set((state) => ({
          pendingOps: state.pendingOps.map(op =>
            op.opId === opId ? { ...op, sent: true } : op
          ),
        }));
      },

      markSnapshotOpSent(opId: string) {
        set((state) => ({
          pendingSnapshotOps: state.pendingSnapshotOps.map(op =>
            op.opId === opId ? { ...op, sent: true } : op
          ),
        }));
      },

      markAllUnsent() {
        set((state) => ({
          pendingOps: state.pendingOps.map(op => ({ ...op, sent: false })),
          pendingSnapshotOps: state.pendingSnapshotOps.map(op => ({ ...op, sent: false })),
        }));
      },

      reset() {
        set({
          entityMeta: new Map(),
          collections: new Map(),
          snapshots: new Map(),
          recordings: new Map(),
          canvases: new Map(),
          spaces: new Map(),
          conversations: new Map(),
          cursor: null,
          pendingOps: [],
          pendingSnapshotOps: [],
        });
      },

      resetEntities() {
        set({
          entityMeta: new Map(),
          collections: new Map(),
          snapshots: new Map(),
          recordings: new Map(),
          canvases: new Map(),
          spaces: new Map(),
          conversations: new Map(),
          // cursor and pendingOps are intentionally preserved
        });
      },

      resetAndApplyBatch(events: EntityEventPayload[], cursor: string) {
        // Single set() — subscribers see one transition (empty → final).
        // Splitting this into resetEntities() + applyBatch() (the old path)
        // surfaced a transient empty state to every subscriber and let
        // racing KIND_SYNC_EVENT handlers apply against a half-populated
        // store mid-bootstrap.
        set(() => {
          const entityMeta = new Map<string, EntityMetaRow>();
          const collections = new Map<string, CollectionRow>();
          const snapshots = new Map<string, SnapshotInfo>();
          const recordings = new Map<string, RecordingMeta>();
          const canvases = new Map<string, CanvasMetaRow>();
          const spaces = new Map<string, SpaceMetaRow>();
          const conversations = new Map<string, ConversationRow>();
          for (const event of events) {
            applyOneEvent({ entityMeta, collections, snapshots, recordings, canvases, spaces, conversations }, event);
          }
          return { entityMeta, collections, snapshots, recordings, canvases, spaces, conversations, cursor };
        });
      },

      reapplyPendingOps() {
        set((state) => {
          const entityMeta = new Map(state.entityMeta);
          const collections = new Map(state.collections);
          const canvases = new Map(state.canvases);
          const spaces = new Map(state.spaces);
          const recordings = new Map(state.recordings);
          const now = new Date().toISOString();

          for (const pending of state.pendingOps) {
            for (const op of pending.ops) {
              const id = op.entityId;

              if (op.action === 'create') {
                // Create: kind tells us which Map to insert into.
                // M9.5-Unify Task 8: rows match the unified BaseEntityRow
                // shape (id, kind, snippet, collectionIds, previewMarkup,
                // previewKind, createdBy, etc.).
                if (op.kind === 'document' && !entityMeta.has(id)) {
                  entityMeta.set(id, {
                    id,
                    title: op.title,
                    kind: 'document',
                    snippet: '',
                    tags: [],
                    isArchived: false,
                    collectionIds: [],
                    previewMarkup: null,
                    previewKind: null,
                    version: 0,
                    createdAt: now,
                    createdBy: null,
                    updatedAt: now,
                    access: { permission: 'owner', grantedAt: now, grantedBy: null, via: 'direct', capabilities: { view: true, edit: true, manage: true, delete: true }, removeSelf: false, removeSelfItemCount: null },
                  });
                } else if (op.kind === 'collection' && !collections.has(id)) {
                  collections.set(id, {
                    id,
                    title: op.title,
                    kind: 'collection',
                    snippet: '',
                    tags: [],
                    isArchived: false,
                    collectionIds: [],
                    previewMarkup: null,
                    previewKind: null,
                    version: 0,
                    createdAt: now,
                    createdBy: null,
                    updatedAt: now,
                    access: { permission: 'owner', grantedAt: now, grantedBy: null, via: 'direct', capabilities: { view: true, edit: true, manage: true, delete: true }, removeSelf: false, removeSelfItemCount: null },
                  });
                }
              } else if (op.action === 'rename') {
                const doc = entityMeta.get(id);
                if (doc) { entityMeta.set(id, { ...doc, title: op.title }); continue; }
                const cv = canvases.get(id);
                if (cv) { canvases.set(id, { ...cv, title: op.title }); continue; }
                const sp = spaces.get(id);
                if (sp) { spaces.set(id, { ...sp, title: op.title }); continue; }
                const col = collections.get(id);
                if (col) { collections.set(id, { ...col, title: op.title }); }
              } else if (op.action === 'archive') {
                const doc = entityMeta.get(id);
                if (doc) { entityMeta.set(id, { ...doc, isArchived: op.archived }); continue; }
                const cv = canvases.get(id);
                if (cv) { canvases.set(id, { ...cv, isArchived: op.archived }); continue; }
                const sp = spaces.get(id);
                if (sp) { spaces.set(id, { ...sp, isArchived: op.archived }); continue; }
                const col = collections.get(id);
                if (col) { collections.set(id, { ...col, isArchived: op.archived }); }
              } else if (op.action === 'restore') {
                const doc = entityMeta.get(id);
                if (doc) { entityMeta.set(id, { ...doc, isArchived: false }); continue; }
                const cv = canvases.get(id);
                if (cv) { canvases.set(id, { ...cv, isArchived: false }); continue; }
                const sp = spaces.get(id);
                if (sp) { spaces.set(id, { ...sp, isArchived: false }); continue; }
                const col = collections.get(id);
                if (col) { collections.set(id, { ...col, isArchived: false }); }
              } else if (op.action === 'delete') {
                entityMeta.delete(id);
                canvases.delete(id);
                spaces.delete(id);
                collections.delete(id);
                recordings.delete(id);
              } else if (op.action === 'setTags') {
                const doc = entityMeta.get(id);
                if (doc) { entityMeta.set(id, { ...doc, tags: op.tags }); continue; }
                const cv = canvases.get(id);
                if (cv) { canvases.set(id, { ...cv, tags: op.tags }); continue; }
                const sp = spaces.get(id);
                if (sp) { spaces.set(id, { ...sp, tags: op.tags }); continue; }
                const col = collections.get(id);
                if (col) { collections.set(id, { ...col, tags: op.tags }); }
              } else if (op.action === 'setCollectionIds') {
                const doc = entityMeta.get(id);
                if (doc) { entityMeta.set(id, { ...doc, collectionIds: op.collectionIds }); continue; }
                const cv = canvases.get(id);
                if (cv) { canvases.set(id, { ...cv, collectionIds: op.collectionIds }); continue; }
                const sp = spaces.get(id);
                if (sp) { spaces.set(id, { ...sp, collectionIds: op.collectionIds }); }
              } else if (op.action === 'updatePreview') {
                const doc = entityMeta.get(id);
                if (doc) {
                  entityMeta.set(id, { ...doc, previewMarkup: op.markup, previewKind: op.kind as 'html' | 'svg' | null });
                  continue;
                }
                const cv = canvases.get(id);
                if (cv) {
                  canvases.set(id, { ...cv, previewMarkup: op.markup, previewKind: op.kind as 'svg' | 'html' | null });
                  continue;
                }
                const sp = spaces.get(id);
                if (sp) {
                  spaces.set(id, { ...sp, previewMarkup: op.markup, previewKind: op.kind as 'svg' | 'html' | null });
                }
              }
            }
          }
          return { entityMeta, collections, canvases, spaces, recordings };
        });
      },

      setEntityRemovedCallback(cb: (id: string) => void) {
        _onEntityRemoved = cb;
      },
    }),
    {
      // The persist `name` is used as the IDB key within the store. Setting it
      // to the workspaceId makes the key stable and deterministic per workspace.
      name: workspaceId,
      // version: 5 — NOT bumped when `canvases` and `spaces` Maps were added in
      // M9.5-Unify. Intentional: the migration path at version < 2 already
      // seeds those Maps with `new Map()` (see migrateEntityStore), so clients
      // that restore a pre-canvas persisted slice will receive the correct
      // empty-Map default without a forced wipe. Bumping the version would clear
      // every user's cached entity list unnecessarily.
      version: 5,
      migrate: migrateEntityStore,
      storage: createJSONStorage(() => IDB_AVAILABLE ? createIdbStorage(dbName) : localStorage, {
        replacer: mapReplacer,
        reviver: mapReviver,
      }),
      // Exclude action functions from persistence; strip ephemeral sent flag from pendingOps
      partialize: (state) => ({
        entityMeta: state.entityMeta,
        collections: state.collections,
        snapshots: state.snapshots,
        recordings: state.recordings,
        canvases: state.canvases,
        spaces: state.spaces,
        conversations: state.conversations,
        cursor: state.cursor,
        pendingOps: state.pendingOps.map(op => ({ ...op, sent: false })),
        pendingSnapshotOps: state.pendingSnapshotOps.map(op => ({ ...op, sent: false })),
      }),
    },
  ),
  // Cast: persist middleware attaches .persist to the returned StoreApi.
  // We narrow to only the persist methods we actually use: getOptions() and
  // rehydrate(). Type safety is preserved via the function return annotation.
  ) as StoreApi<EntityStoreState> & { persist: { getOptions(): { name: string }; rehydrate(): Promise<void> } };
}

// ---------------------------------------------------------------------------
// Active store — global singleton slot; swapped on workspace switch
// ---------------------------------------------------------------------------

// _activeStore is null until setActiveEntityStore() is called by AuthSyncBridge
// after the workspace list resolves. Module import creates NO IndexedDB —
// the first IDB is nula-entity-store-<workspaceId>, opened at auth time.
let _activeStore: ReturnType<typeof createEntityStore> | null = null;

/**
 * Set (or replace) the active entity store for the given workspace.
 * Called by the A1 subscription in useWorkspaceStore after the personal
 * workspace resolves on auth bootstrap (and on every subsequent workspace
 * switch). The previous store is garbage-collected; its IDB connection is
 * released when IndexedDB closes the underlying transaction handles.
 */
export function setActiveEntityStore(workspaceId: string): void {
  // Defensive: empty / undefined workspaceId would create
  // `nula-entity-store-undefined` IDB. Treat as a programmer error.
  if (typeof workspaceId !== 'string' || workspaceId.length === 0) {
    throw new Error(`setActiveEntityStore: invalid workspaceId '${String(workspaceId)}'`);
  }
  _activeStore = createEntityStore(workspaceId);
}

/**
 * Tear down the active entity store on logout. Sets _activeStore back to
 * null so that any post-logout access throws rather than reading stale data.
 * Call this from clearUserData() before clearing auth state.
 */
export function deactivateEntityStore(): void {
  _activeStore = null;
}

/**
 * Get the currently-active entity store. Throws if called before
 * setActiveEntityStore() has fired — all callers are gated behind the
 * post-auth React tree or WS frame handlers, so this is always safe.
 *
 * Throwing (rather than silently returning a placeholder) surfaces
 * accidental pre-auth access at call time rather than producing silent
 * data-loss via writes to a throwaway IDB.
 */
export function getActiveEntityStore(): ReturnType<typeof createEntityStore> {
  if (_activeStore === null) {
    throw new Error('entityStore: no active workspace yet — caller must wait for AuthSyncBridge to fire setActiveEntityStore');
  }
  return _activeStore;
}

/**
 * Best-effort drain of pending ops before a workspace switch. Fires a
 * brief wait to let any in-flight WS sends flush, then resolves. The
 * switch orchestrator calls this fire-and-forget; failure is logged but
 * does not block the switch.
 *
 * Full drain semantics (wait for ACK) are not implemented — the WS
 * ACK loop lives in opSyncBridge and is not accessible from here without
 * circular deps. Pre-launch migration freedom applies: unACK'd ops at
 * switch time are acceptable.
 */
export async function flushPendingOps(): Promise<void> {
  // Give the WS bridge one event-loop turn to flush any buffered sends.
  await new Promise<void>((resolve) => setTimeout(resolve, 50));
}

// ---------------------------------------------------------------------------
// useEntityStore — proxy hook + store API
//
// Preserves the exact public API that all existing consumers expect:
//   - useEntityStore(selector)         — React hook
//   - useEntityStore.getState()        — synchronous read
//   - useEntityStore.setState(...)     — direct state mutation
//   - useEntityStore.subscribe(...)    — subscribe to state changes
//
// All calls are delegated to the currently-active store (set by the switch
// orchestrator via setActiveEntityStore). Callers that run before
// setActiveEntityStore() fires will see getActiveEntityStore() throw — all
// legitimate callers are gated behind the post-auth React tree or WS frame
// handlers, so the throw surfaces accidental pre-auth access rather than
// silently routing writes to a dead store.
// ---------------------------------------------------------------------------

type EntityStoreHook = {
  <T>(selector: (state: EntityStoreState) => T): T;
  getState: () => EntityStoreState;
  setState: StoreApi<EntityStoreState>['setState'];
  subscribe: StoreApi<EntityStoreState>['subscribe'];
  getInitialState: () => EntityStoreState;
};

function useEntityStoreInternal<T>(selector: (state: EntityStoreState) => T): T {
  // React hook path: use Zustand's useStore with the active vanilla store.
  return useStore(getActiveEntityStore(), selector);
}

// Attach store-API methods to the hook function so callers can do
// useEntityStore.getState() etc. without changing their import.
// Each getter caches the result of getActiveEntityStore() to a local
// variable — the double-call pattern `fn().method.bind(fn())` was
// redundant and risked a race if _activeStore changed mid-getter.
Object.defineProperties(useEntityStoreInternal, {
  getState: {
    get() { const s = getActiveEntityStore(); return s.getState.bind(s); },
    enumerable: false,
    configurable: true,
  },
  setState: {
    get() { const s = getActiveEntityStore(); return s.setState.bind(s); },
    enumerable: false,
    configurable: true,
  },
  subscribe: {
    get() { const s = getActiveEntityStore(); return s.subscribe.bind(s); },
    enumerable: false,
    configurable: true,
  },
  getInitialState: {
    get() { const s = getActiveEntityStore(); return s.getInitialState.bind(s); },
    enumerable: false,
    configurable: true,
  },
});

export const useEntityStore = useEntityStoreInternal as EntityStoreHook;

// ---------------------------------------------------------------------------
// Entity-kind resolver
// ---------------------------------------------------------------------------

/**
 * Resolves the entity kind for a given entity id by checking each store Map.
 * Returns the kind string or null if the id is not present in any Map.
 *
 * Reads from the store synchronously — do NOT call inside a render path that
 * subscribes to the store (use store selectors instead).
 *
 * Lives next to the store rather than in @hooks/navigation because it is not
 * a hook and other modules (router, services, etc.) need it without taking
 * a navigation dependency.
 */
export type ResolvedEntityKind =
  'document' | 'collection' | 'recording' | 'canvas' | 'space' | 'conversation';

/**
 * Resolve an entity's kind from the store. Returns null if the id isn't
 * in any Map. Recordings live in their own Map (not BaseEntityRow-shaped),
 * so this function checks recordings explicitly before falling through to
 * the BaseEntityRow accessor.
 */
export function resolveEntityKind(id: string): ResolvedEntityKind | null {
  const state = useEntityStore.getState();
  if (state.recordings.has(id)) return 'recording';
  return (getEntityRow(state, id)?.kind as ResolvedEntityKind | undefined) ?? null;
}

// ---------------------------------------------------------------------------
// Kind-agnostic accessors
//
// Use these in preference to direct `state.entityMeta.get(id)` / etc. chains.
// They eliminate the recurring bug class where adding a new entity kind
// requires hunting down every place that hard-codes which Maps to check.
//
// All accessors return `BaseEntityRow` for non-recording kinds. Recording is
// excluded because `RecordingMeta` is intentionally bespoke (see
// docs/4-engineering/architecture/entity-events.md). Consumers that need
// recording-specific fields should read `recordings.get(id)` directly.
// ---------------------------------------------------------------------------

/**
 * Single source of truth for which store fields hold BaseEntityRow-shaped Maps.
 *
 * Adding a new BaseEntityRow kind = add its store field here, and every
 * kind-agnostic accessor + projection picks it up automatically. This is the
 * one place to update; `getEntityRow`, `useEntityRow`, and the documents
 * projection (`entityProjection.docMapsFromState`) all derive from this list.
 *
 * Order matters only for tie-breaking: when an id somehow appears in multiple
 * Maps, the earlier entry wins. In practice ids are unique so order is moot.
 */
export const BASE_ENTITY_MAP_KEYS = [
  'entityMeta',
  'collections',
  'canvases',
  'spaces',
  'conversations',
] as const satisfies ReadonlyArray<keyof EntityStoreState>;

type BaseEntityMapKey = (typeof BASE_ENTITY_MAP_KEYS)[number];

/**
 * Synchronous accessor — returns the BaseEntityRow for `id` from any of the
 * BaseEntityRow-shaped Maps (see `BASE_ENTITY_MAP_KEYS`). Returns null if the
 * id is not in any Map OR if it resolves to a recording (recording rows have
 * a different shape).
 *
 * Use from non-React code. From React components, use `useEntityRow` so
 * the component re-renders on store mutation.
 */
export function getEntityRow(state: EntityStoreState, id: string): BaseEntityRow | null {
  for (const k of BASE_ENTITY_MAP_KEYS) {
    const row = (state[k] as Map<string, BaseEntityRow>).get(id);
    if (row) return row;
  }
  return null;
}

/**
 * Hook variant of `getEntityRow`. Re-renders when the row's containing Map
 * mutates. Returns null if the id is not in any BaseEntityRow-shaped Map.
 */
export function useEntityRow(id: string | null | undefined): BaseEntityRow | null {
  return useEntityStore((s) => (id ? getEntityRow(s, id) : null));
}

// Re-export the key type so external modules (like entityProjection's
// docMapsFromState filter) can constrain their inputs.
export type { BaseEntityMapKey };
